<template>
  <div
    id="collection-attribute">
    <v-row
      align="center"
      class="mb-4 px-4">
      <v-col cols="auto">
        <h2 class="capitalize mb-0">
          Collection
        </h2>
      </v-col>

      <v-spacer />

      <v-col
        cols="auto"
        class="d-flex align-center">
        <v-btn
          :loading="isExporting"
          color="success"
          :disabled="isExporting"
          @click="exportCollection">
          <v-icon>mdi-microsoft-excel</v-icon>
          EXPORT
        </v-btn>
        <v-dialog
          v-model="dialog"
          max-width="500px">
          <template #activator="{ on, attrs }">
            <v-btn
              color="primary"
              dark
              class="ml-2 mr-2"
              v-bind="attrs"
              v-on="on">
              New Item
            </v-btn>
          </template>
          <v-card>
            <v-form
              ref="attributeForm"
              lazy-validation
              @submit.prevent="save()">
              <v-card-title>
                <span class="headline">{{ formTitle }}</span>
              </v-card-title>
              <v-card-text>
                <v-container>
                  <v-row
                    v-if="dialog"
                    no-gutters>
                    <v-col
                      cols="12"
                      class="mt-2">
                      <v-text-field
                        v-model="formData.name"
                        outlined
                        dense
                        :rules="[ ...rules.required ]"
                        :readonly="modalLoading"
                        label="Name"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      class="mt-2">
                      <v-text-field
                        v-model="formData.nickname"
                        outlined
                        dense
                        :rules="[ ...rules.required, maxLengthRule(20) ]"
                        :readonly="modalLoading"
                        label="Nickname"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      class="mt-2">
                      <v-select
                        v-model="formData.squad"
                        outlined
                        dense
                        :items="squads"
                        :rules="[ ...rules.required ]"
                        :readonly="modalLoading"
                        :menu-props="{ offsetY: true }"
                        label="Squad"
                        item-text="name"
                        item-value="id"
                      >
                      </v-select>
                    </v-col>
                    <v-col
                      cols="12"
                      class="mt-2">
                      <v-select
                        v-model="formData.foreignMerchandiser"
                        outlined
                        dense
                        :items="foreignMerchandisers"
                        :readonly="modalLoading"
                        :menu-props="{ offsetY: true }"
                        label="Merchandiser(CN)"
                        item-text="name"
                        item-value="name"
                      >
                      </v-select>
                    </v-col>
                    <v-col
                      cols="12"
                      class="mt-2">
                      <v-select
                        v-model="formData.classification"
                        outlined
                        dense
                        :items="selectOptions.classifications"
                        :rules="[ ...rules.required ]"
                        :readonly="modalLoading"
                        :menu-props="{ offsetY: true }"
                        label="Classification"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      class="mt-2">
                      <v-select
                        v-model="formData.brand"
                        outlined
                        dense
                        :items="selectOptions.brands"
                        :readonly="modalLoading"
                        :rules="[ ...rules.required ]"
                        :menu-props="{ offsetY: true }"
                        label="Brand"
                        item-text="name"
                        item-value="name"
                      />
                    </v-col>
                    <v-col
                      v-if="!isEdited"
                      cols="12"
                      class="mt-2 mb-6">
                      <date-picker
                        v-model="formData.salesDate"
                        outlined
                        dense
                        label="Sale Date"
                        :rules="[ ...rules.required ]"
                        :show-out-side-label="false"
                      />
                    </v-col>
                    <v-col
                      cols="12"
                      class="mt-2">
                      <v-text-field
                        v-model.number="formData.targetStockValue"
                        outlined
                        dense
                        :rules="stockTargetRules"
                        :readonly="modalLoading"
                        label="Target Stock Value"
                      />
                    </v-col>
                    <v-col
                      v-if="!isEdited"
                      class="mt-2"
                      cols="12">
                      <v-text-field
                        v-model="formData.numberOfPrototype"
                        outlined
                        dense
                        type="text"
                        inputmode="numeric"
                        :readonly="modalLoading"
                        label="จำนวนแบบใน Collection"
                        :rules="[
                          v => v === '' || v === null || /^[0-9]+$/.test(v) || 'กรอกได้เฉพาะตัวเลขจำนวนเต็ม 0-100',
                          v => v === '' || Number(v) <= 100 || 'กรอกได้เฉพาะตัวเลขจำนวนเต็ม 0-100',
                        ]"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn
                  color="blue darken-1"
                  text
                  @click="dialog = false">
                  Cancel
                </v-btn>
                <v-btn
                  :loading="modalLoading"
                  color="blue darken-1"
                  type="submit"
                  text>
                  Save
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
    <v-data-table
      :headers="header"
      :items="data"
      :options.sync="options"
      :items-per-page="options.itemsPerPage"
      :loading="loading"
      :server-items-length="total"
      :height="530"
      class="elevation-0 mb-5"
      hide-default-footer
      @update:page="optionsUpdated()"
      @update:sort-by="optionsUpdated()"
      @update:sort-desc="optionsUpdated()">
      <template #top>
        <v-toolbar flat>
          <v-spacer />
          <div class="mr-2">
            <v-select
              v-model="filterSquad"
              :items="squads"
              :menu-props="{ offsetY: true }"
              hide-details
              dense
              outlined
              clearable
              item-text="name"
              item-value="id"
              label="Squad"
              :loading="loading" />
          </div>
          <search-box
            v-model="search"
            @on-search="searchCollection($event)" />
        </v-toolbar>
      </template>
      <template
        #[`item.bcSync`]="{ item }"
        class="d-flex justify-center">
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <v-icon
              v-if="item.bcSync && item.bcSync.sync && item.bcSync.bcLog"
              v-bind="attrs"
              color="success"
              v-on="on">
              mdi-check-circle-outline
            </v-icon>
            <v-icon
              v-else
              v-bind="attrs"
              color="error"
              v-on="on">
              mdi-sync-alert
            </v-icon>
          </template>
          <span v-if="item.bcSync && item.bcSync.updatedAt">
            Last sync date {{ item.bcSync.updatedAt | dateTimeUserFormat() }}
          </span>
          <span v-else>
            Not sync yet
          </span>
        </v-tooltip>
      </template>
      <template
        #[`item.targetStockValue`]="{ item }"
      >
        <p
          v-if="!!item.targetStockValue"
          class="mb-0">
          {{ item.targetStockValue | showNumberFormat }}
        </p>
        <p
          v-else
          class="mb-0">
          ไม่ระบุ
        </p>
      </template>
      <template
        #[`item.salesDate`]="{ item }"
        class="d-flex justify-center">
        <u
          v-if="userIsCanChangeTaskDate"
          class="primary--text"
          style="cursor: pointer"
          @click="openDialogUpdateSaleDate(item)"
        >
          <span v-if="item.salesDate">
            {{ item.salesDate | dateUserFormat }}
          </span>
          <span v-else>
            ระบุ Sale Date
          </span>
        </u>
        <p
          v-else-if="item.salesDate"
          class="mb-0">
          {{ item.salesDate | dateUserFormat }}
        </p>
        <p
          v-else
          class="mb-0">
          ไม่ระบุ
        </p>
      </template>
      <template #[`item.actions`]="{ item }">
        <v-icon
          small
          class="mr-2"
          @click="editItem(item)">
          mdi-pencil
        </v-icon>
        <gw-icon-hover
          small
          class="sku-table-bin"
          icon-name="mdi-delete"
          icon-hover="mdi-delete-empty"
          @click="removeCollection(item)" />
      </template>
    </v-data-table>
    <v-dialog
      v-if="dialogChangeSaleDate && selectedToChangeSaleDateData"
      v-model="dialogChangeSaleDate"
      max-width="500px"
      @click:outside="resetChangeDateForm()">
      <v-card>
        <v-card-title class="headline">
          Change Sale Date
        </v-card-title>

        <v-form
          ref="saleDateForm"
          v-model="formValid">
          <v-card-text class="pa-6">
            <p class="mb-2">
              Collection: {{ selectedToChangeSaleDateData.name.toUpperCase() }}
            </p>
            <p class="mb-2">
              Current Sale Date: {{ selectedToChangeSaleDateData.salesDate | dateUserFormat }}
            </p>
            <p class="my-2">
              New Sale Date
            </p>
            <date-picker
              v-model="newSalesDate"
              outlined
              :hide-details="false"
              :rules="salesDateRules" />
            <p class="my-2">
              Note
            </p>
            <v-textarea
              v-model="noteChangeSalesDate"
              color="secondary"
              :rules="noteRules"
              dense
              outlined>
            </v-textarea>
          </v-card-text>
        </v-form>

        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            color="primary"
            @click="dialogChangeSaleDate = false">
            Close
          </v-btn>
          <v-btn
            :disabled="loadingChangeSalesDate"
            :loading="loadingChangeSalesDate"
            text
            color="primary"
            @click="changeSaleDate()">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-pagination
      v-if="paginationEnable"
      v-model="options.page"
      :length="pageCount"
      :disabled="loading"
      total-visible="7"
      @input="getCollection()" />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ProductCollectionProvider from '@/resources/ProductCollectionProvider'
import ProductAttributeProvider from '@/resources/ProductAttributeProvider'
import SearchBox from '@/components/SearchBox.vue'
import DatePicker from '@/components/DatePicker.vue'
import PrototypeAttributeProvider from '@/resources/PrototypeAttributeProvider'
import ProductionPlanProvider from '@/resources/ProductionPlanProvider'
import { escapeCsv } from '@/assets/js/StringFormat'
import { ExportToCsv } from 'export-to-csv'
import { getRole } from '@/assets/js/Authentication'

const ProductCollectionService = new ProductCollectionProvider()
const ProductAttributeService = new ProductAttributeProvider()
const PrototypeAttributeService = new PrototypeAttributeProvider()
const ProductionPlanService = new ProductionPlanProvider()

export default {
  components: {
    SearchBox,
    DatePicker
  },
  data () {
    return {
      dialog: false,
      loading: false,
      total: 0,
      valid: false,
      pageCount: 0,
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      selectOptions: {
        designers: [],
        merchandisers: [],
        graphics: [],
        stylists: [],
        brands: [],
        classifications: ['Core', 'Directional', 'Essential']
      },
      header: [
        {
          text: 'Sync',
          value: 'bcSync',
          sortable: false,
          width: 80
        },
        // {
        //   text: 'ID',
        //   value: 'id',
        //   width: 100
        // },
        {
          text: 'Name',
          value: 'name',
          sortable: false,
          width: 200
        },
        {
          text: 'Nickname',
          value: 'nickname',
          sortable: false,
          width: 200
        },
        {
          text: 'Squad',
          value: 'squad.name',
          sortable: false,
          width: 100
        },
        {
          text: 'Merchandiser(CN)',
          value: 'foreignMerchandiser',
          sortable: false,
          width: 200,
          align: 'center'
        },
        {
          text: 'Classification',
          value: 'classification',
          sortable: false,
          width: 200,
          align: 'center'
        },
        {
          text: 'Target Stock Value',
          value: 'targetStockValue',
          sortable: false,
          width: 150,
          align: 'center'
        },
        {
          text: 'Sale Date',
          value: 'salesDate',
          sortable: false,
          align: 'center',
          width: 150
        },
        {
          text: 'Actions',
          value: 'actions',
          sortable: false,
          width: 100,
          align: 'end'
        }
      ],
      data: [],
      foreignMerchandisers: [
        {
          name: 'MHEE'
        },
        {
          name: 'TEAM'
        },
        {
          name: 'XIAOMO'
        }
      ],
      isEdited: false,
      formData: {
        name: '',
        nickname: '',
        squad: '',
        foreignMerchandiser: '',
        classification: '',
        brand: '',
        salesDate: null,
        numberOfPrototype: null,
        targetStockValue: null
      },
      getting: false,
      modalLoading: false,
      search: '',
      rules: {
        required: [
          (v) => !!v || 'กรุณาระบุ field นี้'
        ]
      },
      squads: [],
      filterSquad: null,
      dateText: '',
      dialogChangeSaleDate: false,
      selectedToChangeSaleDateData: null,
      newSalesDate: null,
      noteChangeSalesDate: '',
      formValid: true,
      salesDateRules: [
        (v) => !!v || 'Sale date is required',
        (v) => this.isDateDifferent(v) || 'New sale date must be different'
      ],
      noteRules: [
        (v) => !!v || 'Note is required',
        (v) => v.length <= 100 || 'Max length 100 characters'
      ],
      stockTargetRules: [
        (v) => !v || /^\d+$/.test(v) || 'กรุณาระบุตัวเลขจำนวนเต็มบวก'
      ],
      loadingChangeSalesDate: false,
      isExporting: false,
      userRole: null
    }
  },
  computed: {
    formTitle () {
      return this.isEdited ? 'Edit Item' : 'New Item'
    },
    paginationEnable () {
      return this.pageCount > 1
    },
    userIsCanChangeTaskDate () {
      return ['merchandising_planner', 'developer', 'management'].includes(this.userRole)
    }
  },
  watch: {
    dialog (val) {
      if (!val) {
        this.closeFormModal()
      }
    },
    filterSquad () {
      this.onFilterChanged()
    }
  },
  mounted () {
    if (this.search === '') {
      this.search = this.$route.query?.search || ''
    }
    this.userRole = getRole()
    this.getCollection()
    this.initSelectOptions()
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setErrorPage: 'Components/setErrorPage',
      setModal: 'Components/setModal'
    }),
    maxLengthRule (length) {
      return (v) => v.length <= length || `Field นี้ต้องยาวไม่เกิน ${length} ตัวอักษร`
    },
    searchCollection (value) {
      this.options.page = 1
      this.search = value

      this.$router.push({
        name: this.$route.name,
        query: { search: value }
      }).catch(() => {})

      this.getCollection()
    },
    async getCollection () {
      if (!this.getting) {
        try {
          this.getting = true
          this.loading = true
          const sortBy = this.options.sortBy[0]
          const sortOrder = this.options.sortDesc[0] ? 'desc' : 'asc'
          const { data } = await ProductCollectionService.getAll({
            ...this.options,
            sortBy,
            sortOrder,
            search: this.search,
            squad: this.filterSquad
          })
          this.total = data.total
          this.pageCount = data.pages
          this.data = data.results
        } catch (error) {
          console.error('getCollection: ', error)
          this.setErrorPage(error.code)
        } finally {
          this.loading = false
          this.getting = false
        }
      }
    },
    async initSelectOptions () {
      try {
        this.loading = true

        const promised = await Promise.all([
          ProductAttributeService.getAllProductAttribute()
        ])
        const squad = await PrototypeAttributeService.getSquad({
          limit: 9999,
          sortBy: 'name',
          sortOrder: 'asc'
        })
        this.squads = squad.data.results
        this.selectOptions.brands = promised[0].data.brands ? promised[0].data.brands : []
      } catch (error) {
        console.error('initSelectOptions', error)
      } finally {
        this.loading = false
      }
    },
    closeFormModal () {
      this.dialog = false
      this.isEdited = false
      this.formData = {
        name: '',
        nickname: '',
        foreignMerchandiser: '',
        classification: '',
        brand: '',
        salesDate: '',
        numberOfPrototype: '',
        squad: null
      }
    },
    editItem (item) {
      this.isEdited = true
      this.formData = { ...item, squad: item?.squad?.id ?? null }
      this.dialog = true
    },
    async save () {
      const valid = await this.$refs['attributeForm'].validate()
      if (!valid) return
      this.modalLoading = true
      if (this.isEdited) {
        await this.updateCollection()
      } else {
        await this.createCollection()
      }
      this.closeFormModal()
      this.modalLoading = false
      this.getCollection()
    },
    async createCollection () {
      try {
        const { data } = await ProductCollectionService.createProductCollection(this.formData)
        if (!data.createPrototypeStatusSuccess) {
          this.$store.dispatch('Components/setSnackbar', {
            value: true,
            message: 'Auto Create Sketch ไม่สำเร็จ กรุณาลบ Collection แล้วสร้างใหม่',
            type: 'error'
          })
        } else if (data) {
          this.$store.dispatch('Components/setSnackbar', {
            value: true,
            message: 'Success',
            type: 'success'
          })
        }
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error code ${error.code}: ${error.message}`,
          type: 'error'
        })
      }
    },
    async updateCollection () {
      try {
        const { data } = await ProductCollectionService.updateProductCollection(this.formData.id, this.formData)
        if (data) {
          this.$store.dispatch('Components/setSnackbar', {
            value: true,
            message: 'Success',
            type: 'success'
          })
        }
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error code ${error.code}: ${error.message}`,
          type: 'error'
        })
      }
    },
    async removeCollection (item) {
      this.setModal({
        value: true,
        title: 'Remove',
        message: `Do you want to remove the ${item.name}`,
        confirmText: 'Remove',
        confirmType: 'error',
        cancelType: '',
        cancelText: 'Cancel',
        onConfirm: () => this.confirmRemoveCollection(item.id)
      })
    },
    async confirmRemoveCollection (id) {
      try {
        const { data } = await ProductCollectionService.deleteProductCollection(id)
        if (data) {
          this.$store.dispatch('Components/setSnackbar', {
            value: true,
            message: 'Success',
            type: 'success'
          })
        }
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Error code ${error.code}: ${error.message}`,
          type: 'error'
        })
      }
      this.getCollection()
    },
    onFilterChanged () {
      this.options.page = 1
      this.getCollection()
    },
    resetChangeDateForm () {
      this.dialogChangeSaleDate = false
      this.selectedToChangeSaleDateData = null
      this.newSalesDate = null
      this.noteChangeSalesDate = ''
    },
    openDialogUpdateSaleDate (collectionInfo) {
      this.selectedToChangeSaleDateData = collectionInfo
      this.dialogChangeSaleDate = true
      this.newSalesDate = collectionInfo.salesDate
      this.noteChangeSalesDate = ''
    },
    async changeSaleDate () {
      const form = this.$refs.saleDateForm

      if (!form.validate()) {
        return
      }

      try {
        await ProductCollectionService.updateCollectionSalesDateById(
          {
            collectionId: this.selectedToChangeSaleDateData.id,
            note: this.noteChangeSalesDate,
            salesDate: this.newSalesDate
          }
        )
        ProductionPlanService.changeCollectionSalesDate(
          {
            collectionId: this.selectedToChangeSaleDateData.id,
            collectionName: this.selectedToChangeSaleDateData.name,
            dateChange: this.$dayjs(this.newSalesDate).startOf('day').toISOString(),
            note: this.noteChangeSalesDate
          }
        )
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'Sale date updated successfully',
          type: 'success'
        })
        this.resetChangeDateForm()
        this.getCollection()
      } catch (error) {
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'Failed to update sale date',
          type: 'error'
        })
      } finally {
        this.loadingChangeSalesDate = false
      }
    },
    isDateDifferent (incoming) {
      if (!incoming) return false

      const current = this.$dayjs(this.selectedToChangeSaleDateData.salesDate).format('YYYY-MM-DD')
      const incomingFormatted = this.$dayjs(incoming).format('YYYY-MM-DD')

      return incomingFormatted !== current
    },
    async exportCollection () {
      try {
        this.isExporting = true
        const { data } = await ProductCollectionService.getAll({
          page: 1,
          limit: 9999,
          sortOrder: 'desc',
          sortBy: 'createdAt',
          search: this.search,
          squad: this.filterSquad
        })
        const exportInfo = data?.results.map((coll) => ({
          'Name': escapeCsv(coll.name),
          'Nickname': escapeCsv(coll.nickname),
          'Squad': coll?.squad?.name ?? '',
          'Merchandiser(CN)': coll?.foreignMerchandiser ?? '',
          'Classification': coll?.classification ?? '',
          'Target Stock value': coll?.targetStockValue ? this.$options.filters.showNumberFormat(coll?.targetStockValue) : 'ไม่ระบุ',
          'Sale Date': coll?.salesDate ? this.$dayjs(coll?.salesDate).format('DD/MM/YYYY') : ''
        }))
        const timeStringFormat = this.$dayjs().format('DD_MM_YYYY_HHmm')
        const options = {
          filename: `collections_export_${timeStringFormat}`,
          showLabels: true,
          useKeysAsHeaders: true
        }
        const csvExporter = new ExportToCsv(options)
        csvExporter.generateCsv(exportInfo)
      } catch (error) {
         this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'Failed to export collection',
          type: 'error'
        })
      } finally {
        this.isExporting = false
      }
    }
  }
}
</script>
